/**
 * Room 状態 (ロビー + プレイ共通)。
 *
 * - WS 接続を 1 つ持つ。 page reload で破棄 / connect(code) で再構築。
 * - 楽観更新 (move): client_id を sendMove の戻り値に持たせ、 pendingMoves に保管。
 *   move_applied で確定して pending から削除、move_rejected で削除 + エラー表示。
 *
 * 盤の中身 (snapshot) はサーバから流れてきた JSON をそのまま保持し、 描画は別途 selector で。
 */

import { create } from 'zustand';
import type { Connection } from '../lib/connection';
import { type LocalSetup, startLocalConnection } from '../lib/local-connection';
import { isMpsContext } from '../lib/mps-context';
import { applyMpsLocalResults, applyMpsMessageToMap, type MpsBoardHandState, type MpsLocalResults } from '../lib/mps-state';
import { parseStoredVrfPair, sameBytes, vrfProofInput, vrfStorageKey } from '../lib/vrf-context';
import { connectRoom, newClientId } from '../lib/ws';
import type {
  BoardId,
  ClientMsg,
  MatchConfig,
  Participant,
  Role,
  ServerMsg,
} from '../proto';
import { vrfEvaluate, vrfGenerateKey, vrfVerify } from '../wasm';

export type { MpsBoardHandState } from '../lib/mps-state';

let connectionVersion = 0;

type Phase = 'connecting' | 'lobby' | 'playing' | 'finished' | 'closed';

interface PendingMove {
  board: BoardId;
  mv: unknown;
}

interface RoomState {
  code: string | null;
  phase: Phase;
  participants: Participant[];
  config: MatchConfig | null;
  you: Role | null;
  snapshot: unknown;
  clock: unknown;
  outcome: string | null;
  finishReason: string | null;
  archiveKey: string | null;
  reconnecting: boolean;
  pending: Map<string, PendingMove>;
  /** 現在保留中のドロー申し出 (= 申し出側の Role)。 */
  pendingDraw: Role | null;
  /** 相手が提案した再戦部屋のコード。 設定されたら Result 画面のボタンが「再戦に同意する」 に切替。 */
  rematchCode: string | null;
  lastError: string | null;

  /** Board ごとの VRF 鍵情報 (board_id → { secret, public, host_pk, guest_pk })。 */
  vrf: Map<number, VrfBoardState>;
  vrfContexts: Map<number, string>;

  /** VRF cheat 検出 board の集合 (= DO から `vrf_cheat_detected` を受けた board)。
   *  in-memory のみ (= リロードで消えてよい)。 次の round で正しい proof が両者
   *  揃って dice 注入されたら自動で remove する。 BackgammonBoard が banner 表示に使う。 */
  vrfCheatBoards: Set<number>;

  /** Mental Poker (docs §8.10) の board×hand ごとの relay 受信状態。
   *  Key = `${board}:${hand}`。 各 phase の broadcast 受信時に entry が更新され、
   *  HeadsUpPokerBoard の useEffect が変更を検知して次の send を打つ。 */
  mps: Map<string, MpsBoardHandState>;

  client: Connection | null;
  /** ローカル CPU 対戦かどうか (resign の REST フォールバックを抑制するため)。 */
  isLocal: boolean;
  connect(code: string): void;
  /** ローカル CPU 対戦を開始 (LocalConnection)。 */
  connectLocal(setup: LocalSetup): void;
  disconnect(): void;
  updateConfig(config: MatchConfig): void;
  start(): void;
  sendMove(board: BoardId, mv: unknown): string;
  resign(): void;
  offerDraw(): void;
  acceptDraw(): void;
  /** 盤単位のメタアクション (Resign / OfferDraw / AcceptDraw / DeclineDraw)。 */
  sendBoardAction(board: BoardId, action: 'Resign' | 'OfferDraw' | 'AcceptDraw' | 'DeclineDraw'): void;
  claimTimeout(): void;
  /** 自分の VRF 鍵 を生成し、 公開鍵を board について publish。 */
  ensureVrfPublished(board: BoardId): void;
  /** 指定 board の指定 round に対する自分の proof を送る。 */
  submitVrfProof(board: BoardId, round: number, matchId: string): void;
  /** 再戦部屋を作成済みの提案者が、 元部屋に new_code を通知する。 */
  offerRematch(newCode: string): void;

  // ===== Mental Poker (= Barnett-Smart full-deck shuffle、 docs §8.10) =====
  /** 自分の ElGamal 公開鍵を board×hand に対して 1 度だけ送信。 */
  sendMpsKeyShare(board: BoardId, hand: number, context: string, publicKey: Uint8Array): void;
  /** 自分の shuffle (= permutation + rerandomize) を 1 round 適用した deck を送信。
   *  host が先 (= 初期 deck から)、 guest が後 (= host deck から)。 各 card は 128 hex。 */
  sendMpsShuffle(board: BoardId, hand: number, context: string, deck: string[], proof: string): void;
  /** 指定 deck index 群への partial share + DLEQ proof を送信 (= 相手へ中継される)。 */
  sendMpsPartialDecrypt(
    board: BoardId,
    hand: number,
    context: string,
    cardIndices: number[],
    shares: string[],
    proofs: string[],
  ): void;
  /** mental poker driver hook が計算結果 (= myHole / revealedBoard / cryptoReady /
   *  cheatDetected) を board×hand に書き込む。 sk は driver の useRef のみで保持、 ここには
   *  絶対に書かない。 docs §8.10.A 駆動要件参照。 */
  setMpsLocalResults(board: BoardId, hand: number, context: string, update: MpsLocalResults): void;
}

interface VrfBoardState {
  context: string;
  secret: Uint8Array | null;
  publicKey: Uint8Array | null;
  proofByRound: Map<number, Uint8Array>;
  hostPublicKey: Uint8Array | null;
  guestPublicKey: Uint8Array | null;
  /** 既に proof を送ったラウンド (重複送信防止)。 */
  submittedRounds: Set<number>;
}

function mpsKey(board: number, hand: number): string {
  return `${board}:${hand}`;
}

export const useRoom = create<RoomState>((set, get) => ({
  code: null,
  phase: 'connecting',
  participants: [],
  config: null,
  you: null,
  snapshot: null,
  clock: null,
  outcome: null,
  finishReason: null,
  archiveKey: null,
  reconnecting: false,
  pending: new Map(),
  pendingDraw: null,
  rematchCode: null,
  lastError: null,
  vrf: new Map(),
  vrfContexts: new Map(),
  vrfCheatBoards: new Set(),
  mps: new Map(),
  client: null,
  isLocal: false,

  connect(code) {
    const version = ++connectionVersion;
    get().client?.close();
    const client = connectRoom(code, {
      onMessage: (msg) => { if (version === connectionVersion) handle(set, get, msg); },
      // intentional close = disconnect() からの呼び出し → 既に Room が unmount
      // しているはずなので phase 遷移は不要。 非意図的 close は WS lib 側が
      // 自動再接続するので reconnecting フラグだけで十分。
      onClose: () => {},
      onReconnect: () => { if (version === connectionVersion) set({ reconnecting: true }); },
      onOpen: () => { if (version === connectionVersion) set({ reconnecting: false }); },
    });
    set({
      code,
      client,
      mps: code === get().code ? get().mps : new Map(),
      vrf: code === get().code ? get().vrf : new Map(),
      vrfContexts: code === get().code ? get().vrfContexts : new Map(),
      vrfCheatBoards: code === get().code ? get().vrfCheatBoards : new Set(),
      isLocal: false,
      phase: 'connecting',
      pending: new Map(),
      lastError: null,
    });
  },

  connectLocal(setup) {
    const version = ++connectionVersion;
    get().client?.close();
    // startLocalConnection は同期で broadcastInitial を呼んで phase='playing' に
    // 進めるため、 先に Phase 等を set してから client を作る。 client は後付け
    // でマージする。 (旧実装の setTimeout(0) ベースは StrictMode 二重 mount で
    // 「準備中…」 のまま固まる回避のため除去済み。)
    set({
      code: setup.id,
      client: null,
      mps: setup.id === get().code ? get().mps : new Map(),
      vrf: new Map(), vrfContexts: new Map(), vrfCheatBoards: new Set(),
      isLocal: true,
      phase: 'connecting',
      pending: new Map(),
      lastError: null,
    });
    const client = startLocalConnection(setup, {
      onMessage: (msg) => { if (version === connectionVersion) handle(set, get, msg); },
      onClose: () => {},
      onOpen: () => { if (version === connectionVersion) set({ reconnecting: false }); },
    });
    set({ client });
  },

  disconnect() {
    connectionVersion += 1;
    get().client?.close();
    // phase / code は変えない: Room.tsx が unmount するため意味がないし、
    // 残った場合も auto-reconnect で復活する方が UX が良い。
    set({ client: null });
  },

  updateConfig(config) {
    get().client?.send({ t: 'config_update', config } satisfies ClientMsg);
  },

  start() {
    get().client?.send({ t: 'start' } satisfies ClientMsg);
  },

  sendMove(board, mv) {
    const client_id = newClientId();
    const next = new Map(get().pending);
    next.set(client_id, { board, mv });
    set({ pending: next });
    get().client?.send({ t: 'move', board, mv, client_id } satisfies ClientMsg);
    return client_id;
  },

  resign() {
    // 二重経路で確実に届ける:
    //  1. WS で送る (open なら通常の broadcast 経由で他クライアントも更新)
    //  2. REST フォールバックも叩く (WS が reconnecting/closed でも届くように)
    get().client?.send({ t: 'resign' } satisfies ClientMsg);
    // ローカル CPU 対戦では REST 経路は無いので叩かない。
    if (get().isLocal) return;
    const code = get().code;
    if (code) {
      void fetch(`/api/rooms/${code}/resign`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    }
  },

  offerDraw() {
    get().client?.send({ t: 'draw_offer' } satisfies ClientMsg);
  },

  acceptDraw() {
    get().client?.send({ t: 'draw_accept' } satisfies ClientMsg);
  },

  sendBoardAction(board, action) {
    get().client?.send({ t: 'board_action', board, action } satisfies ClientMsg);
  },

  claimTimeout() {
    get().client?.send({ t: 'claim_timeout' } satisfies ClientMsg);
  },

  ensureVrfPublished(board) {
    const room = get();
    const context = room.vrfContexts.get(board);
    const role = room.you;
    if (room.isLocal || !context || !room.code || !role || role === 'spectator') return;
    const storageKey = vrfStorageKey(room.code, board, context, role);
    const old = room.vrf.get(board);
    let secret = old?.context === context ? old.secret : null;
    let publicKey = old?.context === context ? old.publicKey : null;
    let saved = parseStoredVrfPair(localStorage.getItem(storageKey));
    if (!secret || !publicKey) {
      if (!saved) {
        const pair = vrfGenerateKey();
        saved = {secret: Array.from(pair.secret), publicKey: Array.from(pair.public)};
        localStorage.setItem(storageKey, JSON.stringify(saved));
      }
      secret = Uint8Array.from(saved.secret);
      publicKey = Uint8Array.from(saved.publicKey);
      const input = vrfProofInput(context, 0, role);
      if (!vrfVerify(publicKey, input, vrfEvaluate(secret, input))) throw new Error('保存したサイコロ鍵が一致しません');
    }
    if (!saved || !sameBytes(saved.secret, secret) || !sameBytes(saved.publicKey, publicKey)) throw new Error('保存したサイコロ鍵が変わりました');
    const entry: VrfBoardState = old?.context === context ? {...old, secret, publicKey} : {context, secret, publicKey, hostPublicKey: null, guestPublicKey: null, submittedRounds: new Set(), proofByRound: new Map()};
    const serverKey = role === 'host' ? entry.hostPublicKey : entry.guestPublicKey;
    if (serverKey && !sameBytes(serverKey, publicKey)) throw new Error('サーバーのサイコロ鍵が一致しません');
    if (old?.secret !== secret || old.publicKey !== publicKey || old.context !== context) {
      const next = new Map(room.vrf); next.set(board, entry); set({vrf: next});
    }
    if (!serverKey) get().client?.send({t: 'vrf_publish_key', board, context, public_key: Array.from(publicKey)} satisfies ClientMsg);
  },

  offerRematch(newCode) {
    get().client?.send({ t: 'rematch', new_code: newCode } satisfies ClientMsg);
  },

  submitVrfProof(board, round, matchId) {
    const room = get();
    const entry = room.vrf.get(board);
    const role = room.you;
    const context = room.vrfContexts.get(board);
    if (room.isLocal || !entry?.secret || !entry.publicKey || !role || role === 'spectator' || room.code !== matchId || !context || entry.context !== context || !entry.hostPublicKey || !entry.guestPublicKey || entry.submittedRounds.has(round)) return;
    const serverKey = role === 'host' ? entry.hostPublicKey : entry.guestPublicKey;
    const saved = parseStoredVrfPair(localStorage.getItem(vrfStorageKey(matchId, board, context, role)));
    if (!saved || !sameBytes(saved.secret, entry.secret) || !sameBytes(saved.publicKey, entry.publicKey) || !sameBytes(serverKey, entry.publicKey)) throw new Error('サイコロの保存鍵またはサーバー鍵が一致しません');
    const proof = entry.proofByRound.get(round) ?? vrfEvaluate(entry.secret, vrfProofInput(context, round, role));
    if (!entry.proofByRound.has(round)) {
      const proofByRound = new Map(entry.proofByRound); proofByRound.set(round, proof);
      const next = new Map(room.vrf); next.set(board, {...entry, proofByRound}); set({vrf: next});
    }
    // A send while reconnecting may be dropped. Cache the exact signature and retry until acknowledged.
    get().client?.send({t: 'vrf_submit_proof', board, context, round, proof: Array.from(proof)} satisfies ClientMsg);
  },

  sendMpsKeyShare(board, hand, context, publicKey) {
    if (get().mps.get(mpsKey(board, hand))?.context !== context) return;
    get().client?.send({
      t: 'mps_key_share',
      board,
      hand,
      context,
      public_key: Array.from(publicKey),
    } satisfies ClientMsg);
  },

  sendMpsShuffle(board, hand, context, deck, proof) {
    if (get().mps.get(mpsKey(board, hand))?.context !== context) return;
    get().client?.send({
      t: 'mps_shuffle',
      board,
      hand,
      context,
      deck,
      proof,
    } satisfies ClientMsg);
  },

  sendMpsPartialDecrypt(board, hand, context, cardIndices, shares, proofs) {
    if (get().mps.get(mpsKey(board, hand))?.context !== context) return;
    get().client?.send({
      t: 'mps_partial_decrypt',
      board,
      hand,
      context,
      card_indices: cardIndices,
      shares,
      proofs,
    } satisfies ClientMsg);
  },

  setMpsLocalResults(board, hand, context, update) {
    const key = mpsKey(board, hand);
    const current = get().mps.get(key);
    const nextEntry = applyMpsLocalResults(current, context, update);
    if (!nextEntry || nextEntry === current) return;
    const next = new Map(get().mps);
    next.set(key, nextEntry);
    set({ mps: next });
  },
}));

function handle(
  set: (partial: Partial<RoomState>) => void,
  get: () => RoomState,
  msg: ServerMsg,
): void {
  switch (msg.t) {
    case 'lobby_state': {
      // Playing/Finished への遷移後に lobby_state を受けても phase は巻き戻さない
      // (再接続時に DO が role 確定のため LobbyState を先送りするので、ここで phase を
      // 上書きすると Play 画面から Lobby 画面に逆戻りしてしまう)。それ以外
      // (connecting/closed/lobby) は lobby に揃える — StrictMode の二重 mount で
      // closed を経由しても recover できるよう permissive に。
      const cur = get().phase;
      const nextPhase: Phase = cur === 'playing' || cur === 'finished' ? cur : 'lobby';
      set({
        phase: nextPhase,
        participants: msg.participants,
        config: msg.config,
        you: msg.you,
        mps: get().you != null && get().you !== msg.you ? new Map() : get().mps,
        vrf: get().you != null && get().you !== msg.you ? new Map() : get().vrf,
        vrfContexts: get().you != null && get().you !== msg.you ? new Map() : get().vrfContexts,
      });
      break;
    }
    case 'config_changed':
      set({ config: msg.config });
      break;
    case 'started':
      set({ phase: 'playing' });
      break;
    case 'state': {
      // 終局済みマッチを再ロードしたとき: snapshot.outcome があれば phase は finished のまま。
      // (DO は State → Finished の順で送るので、State 受信時に既に finished だと分かれば
      // フリッカ無しで Result 画面に直行できる。)
      const cur = get().phase;
      const outcomeFromSnap =
        (msg.snapshot as { outcome?: string | null } | null)?.outcome ?? null;
      const nextPhase: Phase =
        cur === 'finished' || outcomeFromSnap != null ? 'finished' : 'playing';
      set({
        snapshot: msg.snapshot,
        phase: nextPhase,
        ...(outcomeFromSnap && cur !== 'finished' ? { outcome: outcomeFromSnap } : {}),
      });
      break;
    }
    case 'move_applied': {
      // サーバ確定: 該当 client_id を pending から削除する仕組みは Play 画面で snapshot
      // 差し替え時に行う (board/mv が一致したら確定とみなす)。
      set({ clock: msg.clock });
      break;
    }
    case 'move_rejected': {
      const next = new Map(get().pending);
      next.delete(msg.client_id);
      set({ pending: next, lastError: `着手が拒否されました: ${msg.reason}` });
      break;
    }
    case 'clock_tick':
      set({ clock: msg.clock });
      break;
    case 'draw_offered':
      set({ pendingDraw: msg.from });
      break;
    case 'draw_withdrawn':
      set({ pendingDraw: null });
      break;
    case 'vrf_context': {
      if (!isMpsContext(msg.context) || get().vrfContexts.get(msg.board) === msg.context) break;
      const contexts = new Map(get().vrfContexts); contexts.set(msg.board, msg.context);
      const vrf = new Map(get().vrf); vrf.delete(msg.board);
      const flags = new Set(get().vrfCheatBoards); flags.delete(msg.board);
      set({vrfContexts: contexts, vrf, vrfCheatBoards: flags});
      break;
    }
    case 'vrf_keys_ready': {
      if (get().vrfContexts.get(msg.board) !== msg.context) break;
      const bytes = (key: number[]) => key.length === 32 && key.every(byte => Number.isInteger(byte) && byte >= 0 && byte <= 255);
      if (!bytes(msg.host_public_key) || !bytes(msg.guest_public_key)) break;
      const old = get().vrf.get(msg.board);
      const current: VrfBoardState = old?.context === msg.context ? old : {context: msg.context, secret: null, publicKey: null, hostPublicKey: null, guestPublicKey: null, submittedRounds: new Set(), proofByRound: new Map()};
      const next = new Map(get().vrf);
      next.set(msg.board, {...current, hostPublicKey: Uint8Array.from(msg.host_public_key), guestPublicKey: Uint8Array.from(msg.guest_public_key)});
      set({vrf: next});
      break;
    }
    case 'vrf_round_ready': {
      if (get().vrfContexts.get(msg.board) !== msg.context) break;
      const entry = get().vrf.get(msg.board);
      if (entry?.context === msg.context) {
        const vrf = new Map(get().vrf);
        vrf.set(msg.board, {...entry, submittedRounds: new Set([...entry.submittedRounds, msg.round])});
        set({vrf});
      }
      const flags = new Set(get().vrfCheatBoards); flags.delete(msg.board); set({vrfCheatBoards: flags});
      break;
    }
    case 'vrf_cheat_detected': {
      if (get().vrfContexts.get(msg.board) !== msg.context || get().vrfCheatBoards.has(msg.board)) break;
      const flags = new Set(get().vrfCheatBoards); flags.add(msg.board); set({vrfCheatBoards: flags});
      break;
    }
    case 'mps_context':
    case 'mps_keys_ready':
    case 'mps_shuffle_ready':
    case 'mps_partial_decrypt_ready': {
      const current = get().mps;
      const next = applyMpsMessageToMap(current, msg, get().you);
      if (next !== current) set({mps: new Map(next)});
      break;
    }
    case 'finished':
      set({
        phase: 'finished',
        outcome: msg.outcome,
        finishReason: msg.reason ?? null,
        archiveKey: msg.archive_key,
        pendingDraw: null,
      });
      break;
    case 'rematch_offered':
      // 相手が再戦部屋を作成した。 Result 画面のボタンが「再戦に同意する」 に切替わる。
      set({ rematchCode: msg.new_code });
      break;
    case 'error':
      set({ lastError: msg.reason });
      break;
  }
}
